import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import logoOpen from "../assets/logo.png";
import logoClose from "../assets/mindcoders icon.png";
import {
  User,
  BookOpen,
  GraduationCap,
  IndianRupee,
  BadgePercent,
  Users,
  CreditCard,
  ChevronDown,
  ChevronRight,
  GraduationCap as LearningIcon,
  Wallet,
  VideoIcon,
  FileText,
  Briefcase,
  ClipboardList,
  CalendarCheck,
  UserPlus,
  HelpCircle,
  MessageCircle,
} from "lucide-react";
import { ROUTES } from "../routes/routePaths";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { logoutUserAPI } from "../services/apicollection";
import Notiflix from "notiflix";
const MENU_GROUPS = [
  {
    id: "learning",
    label: "Learning",
    icon: LearningIcon,
    items: [
      {
        id: "faculty-management",
        label: "Faculty",
        icon: User,
        path: ROUTES.DASHBOARD_FACULTY_MANAGEMENT,
      },
      {
        id: "course-content-management",
        label: "Course Content",
        icon: BookOpen,
        path: ROUTES.DASHBOARD_COURSE_CONTENT_MANAGEMENT,
      },
      {
        id: "course-management",
        label: "Courses",
        icon: GraduationCap,
        path: ROUTES.DASHBOARD_COURSE_MANAGEMENT,
      },
      {
        id: "batch-management",
        label: "Batches",
        icon: GraduationCap,
        path: ROUTES.DASHBOARD_BATCH_MANAGEMENT,
      },
      {
        id: "attendance-management",
        label: "Attendance",
        icon: CalendarCheck,
        path: ROUTES.DASHBOARD_ATTENDANCE_MANAGEMENT,
      },
      {
        id: "all-students",
        label: "All Students",
        icon: Users,
        path: ROUTES.DASHBOARD_ALL_STUDENTS,
      },
      {
        id: "external-admissions",
        label: "External Admissions",
        icon: CalendarCheck,
        path: ROUTES.DASHBOARD_EXTERNAL_ADMISSIONS,
      },
      {
        id: "enrollment-management",
        label: "Enrollments",
        icon: UserPlus,
        path: ROUTES.DASHBOARD_ENROLLMENT_MANAGEMENT,
      },
      {
        id: "event-management",
        label: "Events ",
        icon: VideoIcon,
        path: ROUTES.DASHBOARD_WEBINAR_MANAGEMENT,
      },
      {
        id: "events-registrations",
        label: "Events Registrations",
        icon: Users,
        path: ROUTES.DASHBOARD_WEBINAR_REGISTRATIONS,
      },
      {
        id: "lecture-management",
        label: "Lectures",
        icon: VideoIcon,
        path: ROUTES.DASHBOARD_LECTURE_MANAGEMENT,
      },
      {
        id: "blog-management",
        label: "Blogs",
        icon: FileText,
        path: ROUTES.DASHBOARD_BLOG_MANAGEMENT,
      },
      {
        id: "demo-video-management",
        label: "Demo Videos",
        icon: VideoIcon,
        path: ROUTES.DASHBOARD_DEMO_VIDEO_MANAGEMENT,
      },
      {
        id: "video-review-management",
        label: "Video Reviews",
        icon: VideoIcon,
        path: ROUTES.DASHBOARD_VIDEO_REVIEW_MANAGEMENT,
      },
      {
        id: "enquiry-management",
        label: "Enquiries",
        icon: FileText,
        path: ROUTES.DASHBOARD_ENQUIRY_MANAGEMENT,
      },
      {
        id: "faq-management",
        label: "Home FAQs",
        icon: HelpCircle,
        path: ROUTES.DASHBOARD_FAQ_MANAGEMENT,
      },
      {
        id: "chatbot-queries",
        label: "Chatbot Queries",
        icon: MessageCircle,
        path: ROUTES.DASHBOARD_CHATBOT_QUERIES,
      },
      {
        id: "placement-management",
        label: "Placements",
        icon: Briefcase,
        path: ROUTES.DASHBOARD_PLACEMENT_MANAGEMENT,
      },
      {
        id: "job-management",
        label: "Jobs",
        icon: ClipboardList,
        path: ROUTES.DASHBOARD_JOB_MANAGEMENT,
      },
      {
        id:"hire-from-us-management",
        label:"Hire From Us",
        icon: Briefcase,
        path: ROUTES.DASHBOARD_HIRE_FROM_US,
      },
      {
        id: "student-management",
        label: "Students",
        icon: Users,
        path: ROUTES.DASHBOARD_STUDENT_MANAGEMENT,
      },
      {
        id: "subscription-management",
        label: "Subscriptions",
        icon: CreditCard,
        path: ROUTES.DASHBOARD_SUBSCRIPTION_MANAGEMENT,
      },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    icon: Wallet,
    items: [
      {
        id: "prebooking-payment",
        label: "Prebooking & Payments",
        icon: CreditCard,
        path: ROUTES.DASHBOARD_PREBOOKING_PAYMENT,
      },
      {
        id: "fees-management",
        label: "Fees",
        icon: IndianRupee,
        path: ROUTES.DASHBOARD_FEES_MANAGEMENT,
      },
      {
        id: "offer-management",
        label: "Offers",
        icon: BadgePercent,
        path: ROUTES.DASHBOARD_OFFER_MANAGEMENT,
      },
    ],
  },
];

const DESKTOP_BREAKPOINT = "(min-width: 1024px)";

function Sidebar({ isOpen, onClose }) {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(DESKTOP_BREAKPOINT);
  const isOverlay = !isDesktop;
  const location = useLocation();

  const [expandedGroups, setExpandedGroups] = useState(() =>
    MENU_GROUPS.reduce((acc, g) => {
      acc[g.id] = g.items.some((item) => location.pathname.includes(item.path));
      return acc;
    }, {}),
  );

  useEffect(() => {
    MENU_GROUPS.forEach((g) => {
      const hasActive = g.items.some((item) =>
        location.pathname.includes(item.path),
      );
      if (hasActive) setExpandedGroups((prev) => ({ ...prev, [g.id]: true }));
    });
  }, [location.pathname]);

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleNavClick = () => {
    if (isOverlay && onClose) onClose();
  };

  const sidebarWidth = isDesktop
    ? isOpen
      ? "w-64"
      : "w-[4.5rem]"
    : "w-72 max-w-[85vw]";
  const translate = isOverlay
    ? isOpen
      ? "translate-x-0"
      : "-translate-x-full"
    : "translate-x-0";
  const showLabels = isDesktop ? isOpen : true;
  const showGroups = isOpen || isOverlay;

  const allItems = MENU_GROUPS.flatMap((g) =>
    g.items.map((item) => ({ ...item, groupId: g.id })),
  );

  const handleLogout = () => {
    Notiflix.Confirm.show(
      "Confirm",
      "Are you sure to Logout from you account?",
      "Logout",
      "Abort",
      async () => {
        await logoutUserAPI();
        window.location.reload(ROUTES.LOGIN);
      },
      () => {
        Notiflix.Notify.failure("Logout aborted! ");
      },
    );
  };

  return (
    <>
      {isOverlay && isOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={onClose}
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
        />
      )}
      <aside
        className={`
          fixed top-0 left-0 z-50 shadow-inner shadow-black/80 h-full bg-[#0f1c2e] text-slate-200 shadow-xl
          transition-all duration-300 ease-out
          ${sidebarWidth}
          ${translate}
        `}
      >
        <div className="flex h-full flex-col">
          <Link
            to={"/"}
            className="flex h-14 shrink-0 items-center justify-center border-b border-[#1d3350] px-2"
          >
            <img
              src={showLabels ? logoOpen : logoClose}
              alt="Logo"
              className={`object-contain transition-all duration-300 ${showLabels ? "h-8 w-[70%]" : "h-9 w-9"}`}
            />
          </Link>

          <nav className="flex-1 overflow-y-auto py-4">
            <div className="space-y-1 px-2">
              {showGroups ? (
                MENU_GROUPS.map((group) => {
                  const GroupIcon = group.icon;
                  const isExpanded = expandedGroups[group.id] !== false;
                  return (
                    <div key={group.id} className="rounded-lg">
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.id)}
                        className="flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-300 transition-colors hover:text-slate-100 hover:bg-cyan-500/20"
                      >
                        <GroupIcon
                          size={20}
                          className="shrink-0 text-[#1ec1d1]"
                        />
                        {showLabels && (
                          <>
                            <span className="flex-1">{group.label}</span>
                            {isExpanded ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </>
                        )}
                      </button>
                      {isExpanded && (
                        <div className="ml-2 mt-0.5 space-y-0.5 border-l border-[#1d3350] pl-2">
                          {group.items.map((item) => {
                            const Icon = item.icon;
                            return (
                              <NavLink
                                key={item.id}
                                to={item.path}
                                end={false}
                                onClick={handleNavClick}
                                className={({ isActive }) =>
                                  `flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                                    isActive
                                      ? "bg-gradient-to-r from-[#0a3355] to-[#1ec1d1]/30 text-white border-l-2 border-[#1ec1d1]"
                                      : "text-slate-400 hover:bg-[#13233a] hover:text-slate-200"
                                  }`
                                }
                              >
                                <Icon size={18} className="shrink-0" />
                                {showLabels && (
                                  <span className="truncate">{item.label}</span>
                                )}
                              </NavLink>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="mt-2 space-y-0.5">
                  {allItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.id}
                        to={item.path}
                        end={false}
                        title={item.label}
                        className={({ isActive }) =>
                          `flex min-h-[44px] items-center justify-center rounded-lg px-2 py-2.5 text-sm transition-colors ${
                            isActive
                              ? "bg-gradient-to-r from-[#0a3355] to-[#1ec1d1]/30 text-white border-l-2 border-[#1ec1d1]"
                              : "text-slate-400 hover:bg-[#13233a] hover:text-slate-200"
                          }`
                        }
                      >
                        <Icon size={20} className="shrink-0" />
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          </nav>
          <div
            className="bg-gradient-to-r from-[#ff9a3c] to-[#ff6f3c] text-[#1a0d02] font-semibold rounded-md p-2 m-3 text-center cursor-pointer hover:brightness-110 transition"
            onClick={handleLogout}
          >
            {showLabels ?(
              <span className="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width={24}
                  height={24}
                  fill="none"
                  stroke="white"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ opacity: 1 }}
                >
                  <path fill="none" d="M12 2v10m6.4-5.4a9 9 0 1 1-12.77.04" />
                </svg>
                Logout
              </span>):
              (<span title="logout" className="flex items-center justify-center truncate gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width={24}
                  height={24}
                  fill="none"
                  stroke="white"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ opacity: 1 }}
                >
                  <path fill="none" d="M12 2v10m6.4-5.4a9 9 0 1 1-12.77.04" />
                </svg>
          
              </span>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;